/** Macro #30 — Territory comment thread (top-level + one-level replies). */
import { useState } from "react";
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from "@tanstack/react-query";
import { cn } from "../cn";
import { socialApi, SocialApiError, shortWallet, type Comment } from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { timeAgo } from "../activity-format";
import { ReportMenu } from "./ReportMenu";
import { HeartIcon, Spinner } from "./social-ui";
import { useI18n } from "../../i18n";

const COMMENT_MAX = 1000;

function Composer({
  onSubmit, placeholder, testId, autoFocus,
}: {
  onSubmit: (body: string) => Promise<unknown>;
  placeholder: string;
  testId: string;
  autoFocus?: boolean;
}) {
  const { t } = useI18n();
  const [body, setBody] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => onSubmit(body.trim()),
    onSuccess: () => { setBody(""); setErr(null); },
    onError: (e) => setErr(e instanceof SocialApiError ? e.message : t("social.comment.postError")),
  });
  const disabled = mut.isPending || body.trim().length === 0;
  return (
    <div className="space-y-1.5">
      <textarea
        data-testid={`${testId}-input`}
        value={body}
        autoFocus={autoFocus}
        maxLength={COMMENT_MAX}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className={cn(
          "w-full resize-y rounded-xl border border-white/12 bg-white/[0.03] px-3 py-2",
          "text-[13px] text-white/90 placeholder:text-white/35 outline-none",
          "focus:border-neon-teal/60",
        )}
      />
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] text-white/30">{body.length}/{COMMENT_MAX}</span>
        <button
          type="button"
          data-testid={`${testId}-submit`}
          disabled={disabled}
          onClick={() => mut.mutate()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border border-neon-teal/55 bg-neon-teal/10 px-3.5 py-1.5",
            "font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-neon-teal",
            "transition-colors hover:bg-neon-teal/20 disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          {mut.isPending && <Spinner />} {t("social.comment.post")}
        </button>
      </div>
      {err && <div data-testid={`${testId}-error`} className="font-mono text-[10px] text-state-danger">{err}</div>}
    </div>
  );
}

function CommentBody({ c }: { c: Comment }) {
  const { t } = useI18n();
  const label = c.status === "removed" ? t("social.comment.removed") : c.body;
  return <p className={cn("mt-1 whitespace-pre-wrap break-words text-[13px]", c.status === "removed" ? "italic text-white/35" : "text-white/85")}>{label}</p>;
}

function CommentRow({ c, identityId, depth = 0 }: { c: Comment; identityId: string; depth?: number }) {
  const { t } = useI18n();
  const { isAuthenticated } = useSession();
  const qc = useQueryClient();
  const [showReply, setShowReply] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  const likeMut = useMutation({
    mutationFn: () => (c.liked_by_me ? socialApi.unlikeComment(c.id) : socialApi.likeComment(c.id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social", "comments", identityId] });
      qc.invalidateQueries({ queryKey: ["social", "replies"] });
    },
  });
  const removeMut = useMutation({
    mutationFn: () => socialApi.removeComment(c.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social", "comments", identityId] });
      qc.invalidateQueries({ queryKey: ["social", "replies"] });
    },
  });

  const repliesQ = useQuery({
    queryKey: ["social", "replies", c.id],
    queryFn: ({ signal }) => socialApi.listReplies(c.id, null, signal),
    enabled: showReplies && depth === 0,
    retry: false,
  });

  const authorLabel = shortWallet(c.author_wallet);

  return (
    <div data-testid="comment-row" data-comment-id={c.id} className={cn(depth > 0 && "ml-5 border-l border-white/10 pl-3")}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <a
              href={`#u/${c.author_wallet}`}
              data-testid="comment-author"
              className="font-mono text-[11px] font-semibold text-white/75 hover:text-neon-teal no-underline"
            >
              {authorLabel}
            </a>
            <span className="font-mono text-[10px] text-white/35">{timeAgo(c.created_at)}</span>
            {c.is_mine && <span className="rounded-full border border-white/12 px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.1em] text-white/45">{t("social.comment.you")}</span>}
          </div>
          <CommentBody c={c} />
          {c.status !== "removed" && (
            <div className="mt-1.5 flex items-center gap-3">
              <button
                type="button"
                data-testid="comment-like-btn"
                data-liked={c.liked_by_me ? "true" : "false"}
                disabled={likeMut.isPending || !isAuthenticated}
                onClick={() => likeMut.mutate()}
                className={cn("inline-flex items-center gap-1 text-[11px] transition-colors",
                  c.liked_by_me ? "text-neon-coral" : "text-white/45 hover:text-white/80", !isAuthenticated && "cursor-not-allowed")}
              >
                <HeartIcon filled={c.liked_by_me} /> {c.like_count}
              </button>
              {depth === 0 && (
                <button
                  type="button"
                  data-testid="comment-reply-btn"
                  onClick={() => { if (!isAuthenticated) { window.location.hash = "auth"; return; } setShowReply((v) => !v); }}
                  className="text-[11px] text-white/45 hover:text-white/80"
                >
                  {t("social.comment.reply")}
                </button>
              )}
              {depth === 0 && c.reply_count > 0 && (
                <button
                  type="button"
                  data-testid="comment-toggle-replies"
                  onClick={() => setShowReplies((v) => !v)}
                  className="text-[11px] text-neon-teal/80 hover:text-neon-teal"
                >
                  {showReplies ? t("social.comment.hide") : t(c.reply_count === 1 ? "social.comment.viewOne" : "social.comment.viewMany", { n: c.reply_count })}
                </button>
              )}
              {c.is_mine && (
                <button
                  type="button"
                  data-testid="comment-remove-btn"
                  disabled={removeMut.isPending}
                  onClick={() => removeMut.mutate()}
                  className="text-[11px] text-white/40 hover:text-neon-coral"
                >
                  {t("social.comment.delete")}
                </button>
              )}
            </div>
          )}
        </div>
        {!c.is_mine && c.status !== "removed" && (
          <ReportMenu targetType="comment" targetId={c.id} authorWallet={c.author_wallet} />
        )}
      </div>

      {showReply && (
        <div className="mt-2" data-testid="reply-composer">
          <Composer
            testId={`reply-${c.id}`}
            autoFocus
            placeholder={t("social.comment.replyPlaceholder")}
            onSubmit={async (b) => {
              await socialApi.createComment(identityId, b, c.id);
              setShowReply(false);
              setShowReplies(true);
              qc.invalidateQueries({ queryKey: ["social", "comments", identityId] });
              qc.invalidateQueries({ queryKey: ["social", "replies", c.id] });
            }}
          />
        </div>
      )}

      {showReplies && depth === 0 && (
        <div className="mt-2 space-y-3" data-testid="reply-list">
          {repliesQ.isLoading && <div className="ml-5 text-[11px] text-white/40">{t("social.comment.loadingReplies")}</div>}
          {repliesQ.data?.items.map((r) => <CommentRow key={r.id} c={r} identityId={identityId} depth={1} />)}
        </div>
      )}
    </div>
  );
}

export function CommentThread({ identityId }: { identityId: string }) {
  const { t } = useI18n();
  const { isAuthenticated } = useSession();
  const qc = useQueryClient();

  const q = useInfiniteQuery({
    queryKey: ["social", "comments", identityId],
    queryFn: ({ pageParam, signal }) => socialApi.listComments(identityId, pageParam as string | null, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

  const comments = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section id="territory-comments" data-testid="comment-thread" className="mt-6 space-y-4">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/55">{t("social.comment.heading")}</h3>

      {isAuthenticated ? (
        <Composer
          testId="comment-top"
          placeholder={t("social.comment.topPlaceholder")}
          onSubmit={async (b) => {
            await socialApi.createComment(identityId, b);
            qc.invalidateQueries({ queryKey: ["social", "comments", identityId] });
          }}
        />
      ) : (
        <a href="#auth" data-testid="comment-signin"
           className="inline-block rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/60 no-underline hover:text-white">
          {t("social.comment.signin")}
        </a>
      )}

      <div className="space-y-4">
        {q.isLoading && <div data-testid="comments-loading" className="text-[12px] text-white/40">{t("social.comment.loading")}</div>}
        {!q.isLoading && comments.length === 0 && (
          <div data-testid="comments-empty" className="text-[12px] text-white/40">{t("social.comment.empty")}</div>
        )}
        {comments.map((c) => <CommentRow key={c.id} c={c} identityId={identityId} />)}
      </div>

      {q.hasNextPage && (
        <button
          type="button"
          data-testid="comments-load-more"
          disabled={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
          className="font-mono text-[11px] uppercase tracking-[0.12em] text-neon-teal/80 hover:text-neon-teal disabled:opacity-40"
        >
          {q.isFetchingNextPage ? t("social.comment.loadingMore") : t("social.comment.loadMore")}
        </button>
      )}
    </section>
  );
}
